/**
 * Admin: instructor applications awaiting verification.
 *
 * The decision is not just "is this a real teacher" but "are these subjects
 * theirs to teach", so the claimed subjects are checkboxes rather than a
 * read-only list — an admin who accepts Mathematics but not Accounting
 * approves the subset instead of rejecting the whole application.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiDelete, apiGet, apiPost } from "../../api/client";

type PendingInstructor = {
  id: string;
  username: string;
  name: string;
  email: string | null;
  created_at: string;
  claimed_subjects: { id: string; title: string }[];
};

export function AdminPendingInstructorsPage() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState("");
  // Per-application subject selection, defaulting to everything claimed.
  const [trimmed, setTrimmed] = useState<Record<string, string[]>>({});

  const { data: pending, isLoading } = useQuery({
    queryKey: ["pending-instructors"],
    queryFn: () => apiGet<PendingInstructor[]>("/api/admin/instructors/pending"),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["pending-instructors"] });

  const approve = useMutation({
    mutationFn: ({ id, subjects }: { id: string; subjects: string[] }) =>
      apiPost(`/api/admin/instructors/${id}/approve`, { subject_ids: subjects }),
    onSuccess: (_d, { id }) => {
      const who = pending?.find((p) => p.id === id);
      setNotice(`${who?.name ?? "Instructor"} approved — they can sign in now.`);
      refresh();
    },
    onError: (e: ApiError) => setNotice(e.message),
  });

  const decline = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/instructors/${id}/decline`),
    onSuccess: () => {
      setNotice("Application declined. The account is deactivated.");
      refresh();
    },
    onError: (e: ApiError) => setNotice(e.message),
  });

  if (isLoading) return <p className="text-muted">Loading applications…</p>;

  const selected = (p: PendingInstructor) =>
    trimmed[p.id] ?? p.claimed_subjects.map((s) => s.id);

  function toggleSubject(p: PendingInstructor, sid: string) {
    const current = selected(p);
    setTrimmed((t) => ({
      ...t,
      [p.id]: current.includes(sid)
        ? current.filter((x) => x !== sid)
        : [...current, sid],
    }));
  }

  return (
    <div className="admin-pending">
      <h1>Approve instructors</h1>
      <p className="text-muted">
        An instructor cannot sign in until you approve them.
      </p>

      {notice && <div className="notice" role="status">{notice}</div>}

      {!pending?.length ? (
        <div className="card">
          <p className="text-muted" style={{ margin: 0 }}>
            Nobody is waiting for approval.
          </p>
        </div>
      ) : (
        <div className="pending-list">
          {pending.map((p) => (
            <div key={p.id} className="card pending-card">
              <div className="pending-head">
                <div>
                  <h3>{p.name}</h3>
                  <p className="text-muted">
                    <code>{p.username}</code>
                    {p.email ? ` · ${p.email}` : " · no email"}
                    {" · applied "}
                    {new Date(p.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <span className="field-label">Claims to teach</span>
              <ul className="claimed-list">
                {p.claimed_subjects.map((s) => (
                  <li key={s.id}>
                    <label className="subject-row">
                      <input
                        type="checkbox"
                        checked={selected(p).includes(s.id)}
                        onChange={() => toggleSubject(p, s.id)}
                      />
                      <span className="subject-name">{s.title}</span>
                    </label>
                  </li>
                ))}
              </ul>

              <div className="pending-actions">
                <button
                  className="btn btn-primary btn-sm"
                  disabled={approve.isPending || selected(p).length === 0}
                  onClick={() =>
                    approve.mutate({ id: p.id, subjects: selected(p) })
                  }
                >
                  {selected(p).length === p.claimed_subjects.length
                    ? "Approve"
                    : `Approve ${selected(p).length} of ${p.claimed_subjects.length}`}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={decline.isPending}
                  onClick={() => decline.mutate(p.id)}
                >
                  Decline
                </button>
                {selected(p).length === 0 && (
                  <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                    Approving needs at least one subject — otherwise decline.
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
